import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'util/UserContext';
import { Feedback } from 'util/constants';
import { Info } from 'lucide-react'; // Using lucide-react icons
import api from 'config/axiosConfig';
import TextEditor from 'components/TextEditor';
import Modal from 'ui/Modal'; // Adjust the import path as necessary
import InputTextArea from 'ui/InputTextArea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from 'ui/InputSelect';
import { PrivacyLevel } from 'util/userUtils';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import EntityAvatar from 'components/Entities/EntityAvatar';

interface Props {
    open: boolean;
    onClose: () => void;
}

const AddFeedback: React.FC<Props> = ({ open, onClose }) => {
    const { currentUser, awaitingUser, setShowAuthModal } = useUser();
    const navigate = useNavigate();
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [category, setCategory] = useState<Feedback['properties']['category']>('');
    const [privacy, setPrivacy] = useState<PrivacyLevel>(currentUser?.properties?.privacy_content_default_audience?.value || 'public');
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [titleValidationMessage, setTitleValidationMessage] = useState<string>('');
    const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
    const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);

    useEffect(() => {
      if (awaitingUser) {
        return;
      }
      if (currentUser && currentUser?.properties?.privacy_content_default_audience) {
        setPrivacy(currentUser?.properties?.privacy_content_default_audience.value);
      } else {
        setPrivacy('public');
      }
    }, [currentUser, awaitingUser]);

    useEffect(() => {
        const isValidSubmit = () => {
            setTitleValidationMessage('');
            setError('');

            // Category validation
            if (!category) {
                setError('Category is required.');
                return false;
            }

            // Title validation
            if (!title || title.length === 0) {
                setTitleValidationMessage('Title is required.');
                return false;
            }

            // Not currently submitting
            if (isSubmitting) {
                return false;
            }

            return true;
        };

        setSubmitEnabled(isValidSubmit());
        setTitleValidationMessage('');
        setError('');
    }, [title, category, isSubmitting]);

    const handleAddFeedback = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        if (!submitEnabled) {
            return;
        }

        setIsSubmitting(true);

        try {
            const feedbackData = {
                title: title.trim(),
                description: description.trim(),
                properties: {
                    privacy: privacy,
                    category: category
                },
                user_id: currentUser.id,
            };

            const response = await api.post<Feedback>('/api/feedbacks', feedbackData);

            const newFeedback = response.data;

            await api.post(`/api/votes/feedback/${newFeedback.id}`, {
                user_id: currentUser.id,
                rel_type: 'user_upvote',
            });

            // Add coauthors if any
            if (coauthors.length > 0) {
                try {
                    await api.post(`/api/coauthors/feedback/${newFeedback.id}/request`, {
                        addedCoauthors: coauthors.map(c => ({
                            id: c.id,
                            type: c.type
                        }))
                    });
                } catch (err) {
                    console.error('Error adding coauthors:', err);
                    // Continue even if coauthors couldn't be added
                }
            }

            setSuccess('Feedback added successfully!');
            setTitle('');
            setDescription('');
            setCategory('');
            onClose();
            navigate(`/feedback/${newFeedback.id}`);
        } catch (err: any) {
            if (err.response && err.response.status === 401) {
                setShowAuthModal(true);
            } else {
                setError('Failed to add feedback. Please try again.');
                setSuccess('');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        onClose();
        setTitle('');
        setDescription('');
        setCategory('');
        setPrivacy('public');
        setError('');
        setSuccess('');
        setTitleValidationMessage('');
        setCoauthors([]);
    };

    const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
        if (!coauthors.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
            setCoauthors([...coauthors, suggestion]);
        }
    };

    const handleCoauthorRemove = (coauthor: CoauthorSuggestion) => {
        setCoauthors(coauthors.filter(c => !(c.id === coauthor.id && c.type === coauthor.type)));
    };

    return (
        <Modal isOpen={open} onClose={handleCloseModal} title="Add feedback">
            <form className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto overflow-x-hidden">
                <div className="flex flex-col flex-grow justify-between mb-4 space-y-4">
                    <div className="flex flex-col flex-grow space-y-4">
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Share your feedback with the team.
                        </p>

                        {/* Title Field */}
                        <div className="form-control">
                            <InputTextArea
                                id="feedback-title"
                                name="feedback-title"
                                labelText="Title"
                                fieldRequired
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                autoComplete={`feedback-title`}
                                required
                                maxLength={300}
                                error={titleValidationMessage}
                                validationState={titleValidationMessage ? 'invalid' : !titleValidationMessage && title && title.length > 0 ? 'valid' : 'none'}
                            />
                        </div>

                        {/* Category Field */}
                        <div className="form-control">
                            <Select
                                onValueChange={(value) => setCategory(value as Feedback['properties']['category'])}
                                required
                                aria-required="true"
                            >
                                <SelectTrigger className="input-select px-3 py-2">
                                    <SelectValue placeholder={<><span className="text-eoe-text-meta dark:text-eoe-dark-text-meta">Category</span><span className="text-warning-red dark:text-dark-warning-red">*</span></>} />
                                </SelectTrigger>
                                <SelectContent className="z-[2000] border border-eoe-border-main dark:border-eoe-dark-border-main">
                                    <SelectItem value="Bug">Bug</SelectItem>
                                    <SelectItem value="Enhancement">Enhancement</SelectItem>
                                    <SelectItem value="Functionality">Functionality</SelectItem>
                                    <SelectItem value="User Interface (UI)">User Interface (UI)</SelectItem>
                                    <SelectItem value="User Experience (UX)">User Experience (UX)</SelectItem>
                                    <SelectItem value="Performance">Performance</SelectItem>
                                    <SelectItem value="Compliance">Compliance</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Coauthors Field */}
                        <div className="form-control">
                            <div className="flex flex-col gap-2">
                                <CoauthorSelect
                                    inputType="add"
                                    placeholder="Add collaborators"
                                    className="w-full"
                                    onSelect={handleCoauthorSelect}
                                    excludeIds={coauthors.map(c => c.id)}
                                />
                                {coauthors.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {coauthors.map((coauthor) => (
                                            <EntityAvatar
                                                key={`${coauthor.type}-${coauthor.id}`}
                                                entity={coauthor as any}
                                                entityType={coauthor.type}
                                                entityChip={true}
                                                showLink={false}
                                                showCloseChip={true}
                                                onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <EntityPrivacy
                            privacy={privacy}
                            onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                            actorType="user"
                            editMode={false}
                            entityType="feedback"
                            miniIcon={false}
                            showDropdown={true}
                        />

                        {/* Description Field */}
                        <div className="form-control">
                            <TextEditor
                                value={description}
                                onChange={setDescription}
                                editorType="entity"
                                placeholder={`Description`}
                            />
                        </div>

                        {/* Error and Success Messages */}
                        {(error || success) && (
                            <div className="flex items-center space-x-2">
                                {error && (
                                    <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                        <Info className="mr-1 size-4" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                {success && (
                                    <div className="flex items-center text-green-600 text-sm">
                                        <Info className="mr-1" />
                                        <span>{success}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-grow flex-col pb-4 justify-end place-items-end mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="btn-secondary w-full sm:w-fit py-2 px-4"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            onClick={handleAddFeedback}
                            disabled={isSubmitting || !submitEnabled}
                            className={`w-full sm:w-fit py-2 px-4 ${
                                isSubmitting || !submitEnabled
                                    ? 'btn-primary-disabled'
                                    : 'btn-primary'
                            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default AddFeedback;